import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { X, Settings, ArrowUpDown } from 'lucide-react';
import BookingWidget from '../components/BookingWidget';
import { staysApi } from '../services/staysApi';
import SearchStayCard from '../components/SearchStayCard';
import SearchFiltersForm from '../components/SearchFiltersForm';
import FilterModal from '../components/modals/FilterModal';
import { createDefaultAdvancedFilters } from '../config/searchFilterDefaults';
import { applyStayAdvancedFilters, getStayMinPrice, sortStays } from '../utils/applyStayAdvancedFilters';
import { getAppliedAdvancedFilterLabels } from '../utils/appliedAdvancedFilterLabels';
import DatePickerModal from '../components/modals/DatePickerModal';
import GuestSelectorModal from '../components/modals/GuestSelectorModal';
import { useDateContext } from '../contexts/DateContext';
import { useGuestContext } from '../contexts/GuestContext';
import { getStoredCity } from '../utils/locationPreference';
import {
  canonicalCityLabel,
  isAllLocationsSelection,
  normalizeCityKey,
} from '../utils/cityUtils';
import { dateToYmd, parseYmdLocal } from '../utils/dateYmd';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedDates, formatDateForDisplay } = useDateContext();
  const { guestData, formatGuestString } = useGuestContext();
  
  // State for search results
  const [stays, setStays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  
  // State for sort modal
  const [showSortModal, setShowSortModal] = useState(false);
  const [sortBy, setSortBy] = useState('newest'); // Default sort option
  
  // State for filter modal
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Desktop results: grid vs list (desktop only)
  const [resultsView, setResultsView] = useState('grid');
  
  // State for date and guest modals
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showGuestSelector, setShowGuestSelector] = useState(false);
  /** When true, confirming dates in the picker opens the guest selector (edit / combined flow). */
  const [datePickerChainGuest, setDatePickerChainGuest] = useState(false);
  
  // Location: query string (shared links) or navbar city from storage — both normalized for case-insensitive search
  const [filters, setFilters] = useState(() => {
    const sp = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : ''
    );
    const locParam = sp.get('location');
    const location =
      locParam && String(locParam).trim()
        ? canonicalCityLabel(locParam)
        : getStoredCity();
    return {
      location,
      checkIn:
        sp.get('checkIn') ||
        (selectedDates.checkInDate ? dateToYmd(selectedDates.checkInDate) : ''),
      checkOut:
        sp.get('checkOut') ||
        (selectedDates.checkOutDate ? dateToYmd(selectedDates.checkOutDate) : ''),
      guests: parseInt(sp.get('guests'), 10) || guestData.adults + guestData.children,
      rooms: parseInt(sp.get('rooms'), 10) || guestData.rooms,
      adults: parseInt(sp.get('adults'), 10) || guestData.adults,
      children: parseInt(sp.get('children'), 10) || guestData.children,
      pets: parseInt(sp.get('pets'), 10) || guestData.pets
    };
  });
  
  // Sync filters with global context when they change (if no search params)
  useEffect(() => {
    if (!searchParams.get('checkIn') && !searchParams.get('checkOut') && selectedDates.checkInDate && selectedDates.checkOutDate) {
      setFilters(prev => ({
        ...prev,
        checkIn: dateToYmd(selectedDates.checkInDate) || '',
        checkOut: dateToYmd(selectedDates.checkOutDate) || ''
      }));
    }
  }, [selectedDates, searchParams]);
  
  useEffect(() => {
    if (!searchParams.get('guests') && !searchParams.get('rooms') && !searchParams.get('adults')) {
      setFilters(prev => ({
        ...prev,
        guests: guestData.adults + guestData.children,
        rooms: guestData.rooms,
        adults: guestData.adults,
        children: guestData.children,
        pets: guestData.pets
      }));
    }
  }, [guestData, searchParams]);

  useEffect(() => {
    const onCity = (e) => {
      const c = e.detail?.city;
      if (c) {
        setFilters((prev) => ({ ...prev, location: c }));
      }
    };
    window.addEventListener('bfp:city-changed', onCity);
    return () => window.removeEventListener('bfp:city-changed', onCity);
  }, []);
  
  const [advancedFilters, setAdvancedFilters] = useState(() => createDefaultAdvancedFilters());

  useEffect(() => {
    setAdvancedFilters((prev) => {
      if (!prev.primary?.nearMe) return prev;
      return { ...prev, primary: { ...prev.primary, nearMe: null } };
    });
  }, []);

  const [allStays, setAllStays] = useState([]);

  const filterCityOptions = useMemo(() => {
    const uniq = new Set();
    allStays.forEach((s) => {
      if (s.city) uniq.add(s.city);
    });
    return Array.from(uniq).sort((a, b) => a.localeCompare(b));
  }, [allStays]);
  
  // Parse location to city and state
  const parseLocation = useCallback((location) => {
    if (!location || isAllLocationsSelection(location)) return { city: '', state: '' };
    
    // Handle common location formats
    const parts = location.split(',').map(part => part.trim());
    if (parts.length >= 2) {
      return { city: parts[0], state: parts[1] };
    } else {
      // If only one part, check if it's a state or city
      const singleLocation = parts[0];
      
      // Common states in India
      const states = [
        'Maharashtra', 'Delhi', 'Goa', 'Rajasthan', 'Karnataka', 
        'Tamil Nadu', 'Kerala', 'Gujarat', 'Punjab', 'Haryana'
      ];
      
      // If it's a state, search by state only
      if (states.some(state => 
        state.toLowerCase() === singleLocation.toLowerCase()
      )) {
        return { city: '', state: singleLocation };
      }
      
      // If it's a city, search by city only
      return { city: singleLocation, state: '' };
    }
  }, []);

  const applyAdvancedFilters = useCallback(
    (staysToFilter) => applyStayAdvancedFilters(staysToFilter, advancedFilters),
    [advancedFilters]
  );

  const appliedFilterLabels = useMemo(
    () => getAppliedAdvancedFilterLabels(advancedFilters),
    [advancedFilters]
  );

  // Search function
  const searchStays = useCallback(async (isLoadMore = false) => {
    try {
      if (!isLoadMore) {
        setLoading(true);
        setError(null);
      }
      
      const { city, state } = parseLocation(filters.location);
      
      // Calculate total guests (excluding pets) and room count
      const totalGuests = (filters.adults || 0) + (filters.children || 0);
      const totalRooms = filters.rooms || 1;
      
      const searchParams = {
        ...(city && { city }),
        ...(state && { state }),
        ...(filters.checkIn && { checkIn: filters.checkIn }),
        ...(filters.checkOut && { checkOut: filters.checkOut }),
        ...(totalGuests > 0 && { guests: totalGuests }),
        ...(totalRooms > 0 && { rooms: totalRooms }),
        ...(filters.adults && { adults: filters.adults }),
        ...(filters.children && { children: filters.children }),
        ...(advancedFilters.primary.minRating > 0 && { minRating: advancedFilters.primary.minRating }),
        ...(advancedFilters.primary.budget > 0 && { maxPrice: advancedFilters.primary.budget * 1000 }),
        limit: 100, // Get more results for client-side filtering
        offset: isLoadMore ? offset : 0
      };
      
      console.log('Searching with params:', searchParams);
      console.log('Advanced Filters:', advancedFilters);
      
      // Check if we have at least some search parameters
      const hasSearchParams = city || state || filters.checkIn || filters.checkOut || filters.guests;
      if (!hasSearchParams) {
        console.warn('No search parameters provided, searching for all stays');
      }
      
      const response = await staysApi.searchStaysAll(searchParams);
      
      console.log('Search API response:', response);
      
      // Check if response has the expected structure
      if (!response || !response.stays) {
        console.error('Invalid API response structure:', response);
        setError('Invalid response from server');
        return;
      }
      
      // Store all stays for client-side filtering
      let fetchedStays = response.stays || [];
      if (!isAllLocationsSelection(filters.location)) {
        const scopeKey = normalizeCityKey(filters.location);
        fetchedStays = fetchedStays.filter((s) => normalizeCityKey(s?.city) === scopeKey);
      }
      
      // Apply advanced filters
      fetchedStays = applyAdvancedFilters(fetchedStays);
      
      // Store all stays
      if (!isLoadMore) {
        setAllStays(fetchedStays);
      } else {
        setAllStays(prev => [...prev, ...fetchedStays]);
      }
      
      if (isLoadMore) {
        const newStays = [...stays, ...fetchedStays];
        setStays(sortStays(newStays, sortBy));
        setOffset((prev) => prev + fetchedStays.length);
      } else {
        setStays(sortStays(fetchedStays, sortBy));
        setOffset(fetchedStays.length);
      }

      setHasMore(false);
      
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search stays');
    } finally {
      setLoading(false);
    }
  }, [filters, offset, parseLocation, sortBy, stays, applyAdvancedFilters]);

  const locationKey = normalizeCityKey(filters.location);

  useEffect(() => {
    searchStays();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-fetch when search filters change
  }, [
    locationKey,
    filters.checkIn,
    filters.checkOut,
    filters.guests,
    filters.rooms,
    filters.adults,
    filters.children
  ]);
  
  // Re-apply filters when advanced filters change (but not on initial mount)
  useEffect(() => {
    if (allStays.length > 0 && !loading) {
      const filtered = applyAdvancedFilters(allStays);
      const sorted = sortStays(filtered, sortBy);
      setStays(sorted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, sortBy]);
  
  // Function to handle back navigation
  const handleBack = () => {
    navigate('/stays');
  };

  // Function to handle load more
  const handleLoadMore = () => {
    if (!loading && hasMore) {
      searchStays(true);
    }
  };

  // Function to handle stay card click
  const handleStayClick = (stayId) => {
    // Navigate to property detail with search params preserved
    // Ensure dates are properly formatted as strings
    const formatDateForURL = (dateValue) => {
      if (!dateValue) return '';
      if (dateValue instanceof Date) {
        return dateToYmd(dateValue) || '';
      }
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      if (typeof dateValue === 'string') {
        return dateToYmd(parseYmdLocal(dateValue)) || dateValue;
      }
      return dateValue;
    };

    const formattedCheckIn = formatDateForURL(filters.checkIn);
    const formattedCheckOut = formatDateForURL(filters.checkOut);
    
    console.log('Navigation debug:', {
      originalCheckIn: filters.checkIn,
      originalCheckOut: filters.checkOut,
      formattedCheckIn,
      formattedCheckOut,
      checkInType: typeof filters.checkIn,
      checkOutType: typeof filters.checkOut
    });
    
    const queryParams = new URLSearchParams({
      checkIn: formattedCheckIn,
      checkOut: formattedCheckOut,
      guests: filters.guests.toString(),
      rooms: filters.rooms.toString(),
      adults: filters.adults.toString(),
      children: filters.children.toString(),
      pets: filters.pets.toString()
    });
    
    console.log('Final query params:', queryParams.toString());
    navigate(`/property/${stayId}?${queryParams.toString()}`);
  };

  // Function to handle sort option change
  const handleSortChange = (newSortOption) => {
    console.log('🎯 handleSortChange called with:', newSortOption);
    console.log('📦 Current stays count:', stays.length);
    setSortBy(newSortOption);
    const sortedStays = sortStays(stays, newSortOption);
    console.log('✅ Sorted stays:', sortedStays.slice(0, 3).map(s => ({ 
      title: s.title, 
      price: getStayMinPrice(s),
      rating: s.rating 
    })));
    setStays(sortedStays);
    setShowSortModal(false);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric',
      month: 'short'
    });
  };

  // Calculate nights
  const calculateNights = () => {
    if (!filters.checkIn || !filters.checkOut) return 0;
    const checkIn = parseYmdLocal(filters.checkIn);
    const checkOut = parseYmdLocal(filters.checkOut);
    if (!checkIn || !checkOut) return 0;
    return Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24));
  };
  
  // Get display dates - prioritize global context (actual selected) over filters
  const getDisplayDates = () => {
    // First check global context (actual selected dates)
    if (selectedDates.checkInDate && selectedDates.checkOutDate) {
      return {
        checkIn: selectedDates.checkInDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }),
        checkOut: selectedDates.checkOutDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
      };
    }
    // Fallback to filters if global context not available
    if (filters.checkIn && filters.checkOut) {
      return {
        checkIn: formatDate(filters.checkIn),
        checkOut: formatDate(filters.checkOut)
      };
    }
    return { checkIn: '', checkOut: '' };
  };
  
  // Get display guests - prioritize global context (actual selected) over filters
  const getDisplayGuests = () => {
    // First check global context (actual selected guests)
    if (guestData && typeof guestData.rooms === 'number' && typeof guestData.adults === 'number') {
      const totalGuests = guestData.adults + (guestData.children || 0);
      return `${guestData.rooms} Room ${totalGuests} guest`;
    }
    // Fallback to filters if global context not available
    if (filters.rooms && filters.adults) {
      const totalGuests = filters.adults + (filters.children || 0);
      return `${filters.rooms} Room ${totalGuests} guest`;
    }
    return 'Select guests';
  };
  
  // Get display location - aligned with the city used for scoping
  const getDisplayLocation = () => {
    return filters.location || getStoredCity();
  };

  return (
    <div className="min-h-screen">
      {/* Desktop: coral band + white booking bar (same pattern as Stays); mobile uses coral pill below */}
      <div className="hidden md:block bg-[#FF5E4C] py-[20px] md:pt-0">
        <BookingWidget />
      </div>
      {/* Mobile: coral header + translucent summary pill (tap dates / guests / edit) */}
      <div className="bg-[#FF5E4C] text-white md:hidden">
        <div className="px-4 pb-4 pt-3">
          <button
            type="button"
            onClick={handleBack}
            className="mb-3 rounded-full p-1 transition-colors hover:bg-white/10"
            aria-label="Back"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" aria-hidden>
              <g clipPath="url(#clip0_sr_mobile_back)">
                <path d="M5 12.8702H19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5 12.8702L11 18.8702" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5 12.8702L11 6.87024" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_sr_mobile_back">
                  <rect width="24" height="24" fill="white" transform="translate(0 0.870239)" />
                </clipPath>
              </defs>
            </svg>
          </button>

          <div className="flex min-h-[52px] w-full items-center gap-1 rounded-[14px] border border-white/25 bg-white/15 px-2.5 py-2 backdrop-blur-sm">
            <div className="flex min-w-0 flex-1 items-stretch justify-between gap-1 text-left text-[12px] font-medium leading-tight text-white/95">
              <div className="flex min-w-0 max-w-[28%] flex-1 items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none" className="shrink-0 text-white" aria-hidden>
                  <g clipPath="url(#clip0_sr_mobile_pin)">
                    <path
                      d="M5.25 7.28687C5.25 7.75099 5.43437 8.19611 5.76256 8.5243C6.09075 8.85249 6.53587 9.03687 7 9.03687C7.46413 9.03687 7.90925 8.85249 8.23744 8.5243C8.56563 8.19611 8.75 7.75099 8.75 7.28687C8.75 6.82274 8.56563 6.37762 8.23744 6.04943C7.90925 5.72124 7.46413 5.53687 7 5.53687C6.53587 5.53687 6.09075 5.72124 5.76256 6.04943C5.43437 6.37762 5.25 6.82274 5.25 7.28687Z"
                      stroke="currentColor"
                      strokeWidth="1.16667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10.3 10.5868L7.82488 13.0619C7.60612 13.2804 7.30955 13.4032 7.00033 13.4032C6.69112 13.4032 6.39455 13.2804 6.17579 13.0619L3.70013 10.5868C3.04751 9.93412 2.60307 9.1026 2.42303 8.19736C2.24298 7.29212 2.33541 6.35382 2.68863 5.50112C3.04184 4.64841 3.63998 3.91959 4.40741 3.40682C5.17483 2.89405 6.07707 2.62036 7.00004 2.62036C7.92301 2.62036 8.82525 2.89405 9.59268 3.40682C10.3601 3.91959 10.9582 4.64841 11.3115 5.50112C11.6647 6.35382 11.7571 7.29212 11.5771 8.19736C11.397 9.1026 10.9526 9.93412 10.3 10.5868Z"
                      stroke="currentColor"
                      strokeWidth="1.16667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_sr_mobile_pin">
                      <rect width="14" height="14" fill="white" transform="translate(0 0.870239)" />
                    </clipPath>
                  </defs>
                </svg>
                <span className="truncate">
                  {(() => {
                    const location = getDisplayLocation();
                    return location.length > 10 ? `${location.substring(0, 10)}…` : location;
                  })()}
                </span>
              </div>

              <button
                type="button"
                className="flex min-w-0 max-w-[36%] flex-1 items-center gap-1 rounded-lg py-1 text-left transition-colors hover:bg-white/10 active:bg-white/15"
                onClick={() => {
                  setDatePickerChainGuest(false);
                  setShowDatePicker(true);
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none" className="shrink-0 text-white" aria-hidden>
                  <g clipPath="url(#clip0_sr_mobile_cal)">
                    <path
                      d="M2.33337 4.95353C2.33337 4.64411 2.45629 4.34737 2.67508 4.12857C2.89388 3.90978 3.19062 3.78687 3.50004 3.78687H10.5C10.8095 3.78687 11.1062 3.90978 11.325 4.12857C11.5438 4.34737 11.6667 4.64411 11.6667 4.95353V11.9535C11.6667 12.263 11.5438 12.5597 11.325 12.7785C11.1062 12.9973 10.8095 13.1202 10.5 13.1202H3.50004C3.19062 13.1202 2.89388 12.9973 2.67508 12.7785C2.45629 12.5597 2.33337 12.263 2.33337 11.9535V4.95353Z"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path d="M9.33337 2.62024V4.95357" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4.66663 2.62024V4.95357" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M2.33337 7.28687H11.6667" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4.66663 9.62024H5.83329V10.7869H4.66663V9.62024Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                  <defs>
                    <clipPath id="clip0_sr_mobile_cal">
                      <rect width="14" height="14" fill="white" transform="translate(0 0.870239)" />
                    </clipPath>
                  </defs>
                </svg>
                <span className="truncate">
                  {(() => {
                    const dates = getDisplayDates();
                    return dates.checkIn && dates.checkOut
                      ? `${dates.checkIn} - ${dates.checkOut}`
                      : 'Select dates';
                  })()}
                </span>
              </button>

              <button
                type="button"
                className="flex min-w-0 max-w-[32%] flex-1 items-center gap-1 rounded-lg py-1 text-left transition-colors hover:bg-white/10 active:bg-white/15"
                onClick={() => setShowGuestSelector(true)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none" className="shrink-0 text-white" aria-hidden>
                  <g clipPath="url(#clip0_sr_mobile_door)">
                    <path
                      d="M8.16663 7.87024V7.87607"
                      stroke="currentColor"
                      strokeWidth="1.16667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path d="M1.75 13.1202H12.25" stroke="currentColor" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                    <path
                      d="M3.5 13.1202V3.78691C3.5 3.47749 3.62292 3.18074 3.84171 2.96195C4.0605 2.74316 4.35725 2.62024 4.66667 2.62024H9.33333C9.64275 2.62024 9.9395 2.74316 10.1583 2.96195C10.3771 3.18074 10.5 3.47749 10.5 3.78691V13.1202"
                      stroke="currentColor"
                      strokeWidth="1.16667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_sr_mobile_door">
                      <rect width="14" height="14" fill="white" transform="translate(0 0.870239)" />
                    </clipPath>
                  </defs>
                </svg>
                <span className="truncate">{getDisplayGuests()}</span>
              </button>
            </div>

            <button
              type="button"
              aria-label="Edit dates and rooms"
              className="shrink-0 rounded-full p-2 transition-colors hover:bg-white/10 active:bg-white/15"
              onClick={() => {
                setDatePickerChainGuest(true);
                setShowDatePicker(true);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M4 20H8L18.5 9.5C18.7626 9.23735 18.971 8.92555 19.1131 8.58239C19.2553 8.23923 19.3284 7.87143 19.3284 7.5C19.3284 7.12857 19.2553 6.76077 19.1131 6.41761C18.971 6.07445 18.7626 5.76265 18.5 5.5C18.2374 5.23735 17.9256 5.02901 17.5824 4.88687C17.2392 4.74473 16.8714 4.67157 16.5 4.67157C16.1286 4.67157 15.7608 4.74473 15.4176 4.88687C15.0744 5.02901 14.7626 5.23735 14.5 5.5L4 16V20Z"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M13.5 6.5L17.5 10.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Date Picker Modal */}
      {showDatePicker && (
        <DatePickerModal 
          onClose={() => {
            setShowDatePicker(false);
            setDatePickerChainGuest(false);
          }}
          initialDates={{
            checkInDate: selectedDates.checkInDate,
            checkOutDate: selectedDates.checkOutDate
          }}
          onOpenGuestSelector={
            datePickerChainGuest
              ? () => {
                  setShowGuestSelector(true);
                }
              : undefined
          }
          onSelect={(dates) => {
            // Global context is already updated by DatePickerModal
            // Update filters and trigger new search
            setFilters(prev => ({
              ...prev,
              checkIn: dateToYmd(dates.checkInDate) || '',
              checkOut: dateToYmd(dates.checkOutDate) || ''
            }));
            // Trigger search with new dates
            // The search will be triggered by the useEffect that watches filters
          }}
        />
      )}

      {/* Guest Selector Modal */}
      {showGuestSelector && (
        <GuestSelectorModal 
          onClose={() => setShowGuestSelector(false)}
          onSelect={(guestString) => {
            // Global context is already updated by GuestSelectorModal
            // Update filters
            setFilters(prev => ({
              ...prev,
              guests: guestData.adults + guestData.children,
              rooms: guestData.rooms,
              adults: guestData.adults,
              children: guestData.children,
              pets: guestData.pets
            }));
            // Trigger search with new guest data
            // The search will be triggered by the useEffect that watches filters
          }}
        />
      )}

      {/* Results: desktop bordered card; mobile cream full-bleed + chip filters */}
      <div className="relative min-h-screen bg-[#F9F8F4] pb-10 pt-0 md:bg-[#ffffff] md:pb-14 ">
        <div className="w-full px-4 pt-[16px] md:pt-0 md:px-[120px] py-0 md:py-[60px]">
          <div className="overflow-hidden rounded-none bg-transparent shadow-none md:flex md:gap-[10px]">
            {/* Desktop: left filter column (~¼ width); mobile filters: chip row → FilterModal */}
            <aside className="hidden w-[280px] shrink-0 border border-[#dddddd] bg-white md:block lg:w-[300px] xl:w-[320px]">
              <div className="sticky top-0  overflow-y-auto h-auto">
                <h2 className=" text-[20px] p-[16px] border-b border-[#dddddd] font-regular tracking-tight text-black">Filter By</h2>
                <SearchFiltersForm
                  value={advancedFilters}
                  onChange={setAdvancedFilters}
                  variant="searchResults"
                  cityOptions={filterCityOptions}
                  onOpenMoreFilters={() => setShowFilterModal(true)}
                  hideNearMe
                />
              </div>
            </aside>

            {/* Main: toolbar + grid */}
            <div className="min-w-0 w-full flex-col flex gap-[10px]">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex w-full min-w-0 items-center justify-between gap-2 md:w-auto md:justify-start">
                  <div className="flex min-w-0 items-center gap-2">
                    <h1 className="truncate text-[19px] font-bold tracking-tight text-black md:font-semibold md:text-[22px]">
                      Your Results
                    </h1>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="23"
                      viewBox="0 0 24 25"
                      fill="none"
                      className="shrink-0 text-[#a3a3a3]"
                      aria-hidden
                    >
                      <g clipPath="url(#clip0_sr_chain_main)">
                        <path
                          d="M15 3.87012C15.7957 3.87012 16.5587 4.18619 17.1213 4.7488C17.6839 5.31141 18 6.07447 18 6.87012C18.4934 6.87001 18.9791 6.99157 19.4142 7.22403C19.8494 7.45649 20.2205 7.79268 20.4947 8.20281C20.7689 8.61294 20.9377 9.08436 20.9861 9.57531C21.0346 10.0663 20.9613 10.5616 20.7726 11.0174C20.5839 11.4733 20.2858 11.8755 19.9045 12.1886C19.5232 12.5017 19.0706 12.7159 18.5868 12.8123C18.103 12.9087 17.6028 12.8843 17.1307 12.7412C16.6585 12.5981 16.229 12.3408 15.88 11.9921L11.122 16.7501C11.4654 17.0938 11.7202 17.5157 11.8645 17.9796C12.0088 18.4435 12.0382 18.9356 11.9503 19.4134C11.8624 19.8912 11.6598 20.3405 11.3598 20.7227C11.0599 21.1049 10.6716 21.4085 10.2284 21.6075C9.78518 21.8064 9.30027 21.8948 8.81536 21.8649C8.33045 21.835 7.86005 21.6878 7.4446 21.436C7.02915 21.1841 6.68108 20.8351 6.43032 20.419C6.17956 20.0029 6.03361 19.5321 6.00502 19.0471V18.8701H5.82902C5.34385 18.8417 4.87286 18.6958 4.45656 18.445C4.04025 18.1943 3.6911 17.8461 3.43915 17.4305C3.1872 17.0149 3.03998 16.5444 3.01017 16.0593C2.98035 15.5742 3.06884 15.0891 3.268 14.6458C3.46717 14.2025 3.77105 13.8142 4.15352 13.5144C4.53598 13.2145 4.98556 13.0121 5.4636 12.9244C5.94163 12.8368 6.43379 12.8666 6.89775 13.0113C7.3617 13.156 7.78356 13.4113 8.12702 13.7551L12.885 8.99712C12.4653 8.57767 12.1794 8.04319 12.0635 7.46125C11.9475 6.87932 12.0068 6.27607 12.2337 5.72781C12.4606 5.17954 12.8451 4.71089 13.3384 4.38111C13.8316 4.05133 14.4116 3.87524 15.005 3.87512L15 3.87012Z"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_sr_chain_main">
                          <rect width="24" height="24" fill="white" transform="translate(0 0.870117)" />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                  <span className="shrink-0 text-[13px] font-medium text-[#737373] md:hidden">
                    {loading ? 'Searching…' : `${stays.length} found`}
                  </span>
                </div>

                <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-0.5 scrollbar-hide md:hidden">
                  <button
                    type="button"
                    onClick={() => setShowFilterModal(true)}
                    className="inline-flex shrink-0 items-center gap-2 rounded-[12px] border border-[#e3e3e3] bg-white px-4 py-2 text-[13px] font-medium text-black"
                  >
                    <Settings size={16} strokeWidth={1.75} className="shrink-0" aria-hidden />
                    Filters
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSortModal(true)}
                    className="inline-flex shrink-0 items-center gap-2 rounded-[12px] border border-[#e3e3e3] bg-white px-4 py-2 text-[13px] font-medium text-black"
                  >
                    <ArrowUpDown size={16} strokeWidth={1.75} className="shrink-0" aria-hidden />
                    Sort by
                  </button>
                  {appliedFilterLabels.map((label, i) => (
                    <button
                      key={`${i}-${label}`}
                      type="button"
                      onClick={() => setShowFilterModal(true)}
                      title={label}
                      className="max-w-[min(200px,46vw)] shrink-0 truncate rounded-full border border-[#e3e3e3] bg-white px-4 py-2 text-left text-[13px] font-medium text-black"
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div className="hidden flex-wrap items-center gap-3 md:flex md:gap-[16px]">
                  <button
                    type="button"
                    onClick={() => setShowSortModal(true)}
                    className="rounded-full border border-[#3b82f6] md:border-[#e3e3e3] md:rounded-[12px] bg-white px-4 md:h-[36px] leading-none text-[13px] font-semibold text-[#2563eb] transition hover:bg-[#eff6ff] md:px-5 md:text-[14px]"
                  >
                    Sort By
                  </button>
                  <span className="text-[13px] font-medium text-[#bebebe]">
                    {loading ? 'Searching…' : `${stays.length} found`}
                  </span>
                  <div className="ml-auto flex items-center rounded-[10px] bg-[#f0f0f0] p-[3px] gap-[4px] h-[36px] rounded-full">
                    <button
                      type="button"
                      onClick={() => setResultsView('list')}
                      className={`rounded-full h-[30px] w-[30px] flex items-center justify-center transition-colors ${
                        resultsView === 'list' ? 'bg-[#ffffff] text-[#555555]' : 'text-[#999999]'
                      }`}
                      aria-label="List view"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        className="shrink-0"
                        aria-hidden
                      >
                        <g clipPath="url(#clip0_sr_results_list)">
                          <path
                            d="M2.25 11.25H15.75M2.25 6.75H15.75M3 15.75C2.80109 15.75 2.61032 15.671 2.46967 15.5303C2.32902 15.3897 2.25 15.1989 2.25 15L2.25 3C2.25 2.80109 2.32902 2.61032 2.46967 2.46967C2.61032 2.32902 2.80109 2.25 3 2.25H15C15.1989 2.25 15.3897 2.32902 15.5303 2.46967C15.671 2.61032 15.75 2.80109 15.75 3V15C15.75 15.1989 15.671 15.3897 15.5303 15.5303C15.3897 15.671 15.1989 15.75 15 15.75H3Z"
                            stroke={resultsView === 'list' ? '#555555' : '#999999'}
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_sr_results_list">
                            <rect width="18" height="18" fill="white" transform="matrix(0 -1 1 0 0 18)" />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => setResultsView('grid')}
                      className={`rounded-full h-[30px] w-[30px] flex items-center justify-center transition-colors ${
                        resultsView === 'grid' ?  'bg-[#ffffff] text-[#555555]' : 'text-[#999999]'
                      }`}
                      aria-label="Grid view"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        className="shrink-0"
                        aria-hidden
                      >
                        <g clipPath="url(#clip0_sr_results_grid)">
                          <path
                            d="M3 3H7.5V7.5H3V3Z"
                            stroke={resultsView === 'grid' ? '#555555' : '#999999'}
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10.5 3H15V7.5H10.5V3Z"
                            stroke={resultsView === 'grid' ? '#555555' : '#999999'}
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M3 10.5H7.5V15H3V10.5Z"
                            stroke={resultsView === 'grid' ? '#555555' : '#999999'}
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10.5 10.5H15V15H10.5V10.5Z"
                            stroke={resultsView === 'grid' ? '#555555' : '#999999'}
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_sr_results_grid">
                            <rect width="18" height="18" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Results list / grid */}
              <div className="">
        {/* Loading state */}
        {loading && stays.length === 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[20px] md:gap-6">
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <div key={item} className="animate-pulse rounded-[12px] border border-[#ececec] bg-white">
                <div className="h-[250px] rounded-t-[12px] bg-gray-300"></div>
                <div className="p-4">
                  <div className="h-5 bg-gray-300 rounded mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error state */}
        {error && stays.length === 0 && (
          <div className="text-center py-12">
            <div className="text-red-500 text-6xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Search Error</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => searchStays()}
              className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* No results */}
        {!loading && !error && stays.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 text-6xl mb-4">🏠</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No stays found</h2>
            <p className="text-gray-600 mb-4">
              There are no stays in {filters.location || getStoredCity()} for these filters. Try changing the
              city in the header, or adjust dates and filters.
            </p>
            <button 
              onClick={handleBack}
              className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Back to Search
            </button>
          </div>
        )}

        {/* Results grid */}
        {stays.length > 0 && (
          <>
            <div
              className={
                resultsView === 'list'
                  ? 'hidden md:flex md:flex-col gap-5'
                  : 'grid grid-cols-1 md:grid-cols-3 gap-[8px]'
              }
            >
              {stays.map((stay) => (
                <SearchStayCard
                  key={stay.stayId}
                  stay={stay}
                  onStayClick={handleStayClick}
                  searchFilters={filters}
                  layout={resultsView === 'list' ? 'list' : 'grid'}
                />
              ))}
            </div>
            {/* Mobile always uses single-column grid */}
            {resultsView === 'list' && (
              <div className="md:hidden grid grid-cols-1 gap-6">
                {stays.map((stay) => (
                  <SearchStayCard
                    key={`m-${stay.stayId}`}
                    stay={stay}
                    onStayClick={handleStayClick}
                    searchFilters={filters}
                    layout="grid"
                  />
                ))}
              </div>
            )}

            {/* Load more button */}
            {hasMore && (
              <div className="text-center mt-8">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="bg-black text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sort Modal — mobile: bottom sheet; desktop: centered */}
      {showSortModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex md:items-center md:justify-center items-end justify-center">
          <div className="bg-white rounded-t-[24px] md:rounded-[24px] w-full max-w-[500px] p-[16px] animate-slide-up md:animate-none md:shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-[20px]">
              <h2 className="text-[20px] font-semibold text-black">Sort By</h2>
              <button 
                onClick={() => setShowSortModal(false)}
                className="text-gray-500 hover:text-black"
              >
                <X size={24} />
              </button>
            </div>

            {/* Sort Options */}
            <div className="mb-[20px] border-[1px] border-[#f0f0f0] rounded-[14px]">
              {/* Newest Listings */}
              <label className="flex items-center gap-[10px] p-[16px] cursor-pointer">
                <div className="h-[24px] w-[24px]">
                  <input
                    type="radio"
                    name="sortBy"
                    value="newest"
                    checked={sortBy === 'newest'}
                    onChange={() => setSortBy('newest')}
                    className="w-[24px] h-[24px] accent-[#FF5E4C]"
                  />

                </div>
                <span className="text-[16px] font-medium leading-none text-black">Newly Listings (Default)</span>
              </label>

              {/* Cheapest */}
              <label className="flex items-center gap-[10px] p-[16px] cursor-pointer">
                <div className="h-[24px] w-[24px]">
                  <input
                    type="radio"
                    name="sortBy"
                    value="cheapest"
                    checked={sortBy === 'cheapest'}
                    onChange={() => setSortBy('cheapest')}
                    className="w-[24px] h-[24px] accent-[#FF5E4C]"
                  />
                </div>
                <span className="text-[16px] text-black">Price: Low to High</span>
              </label>

              {/* Most Expensive */}
              <label className="flex items-center gap-[10px] p-[16px] cursor-pointer">
                <div className="h-[24px] w-[24px]">
                  <input
                    type="radio"
                    name="sortBy"
                    value="expensive"
                    checked={sortBy === 'expensive'}
                    onChange={() => setSortBy('expensive')}
                    className="w-[24px] h-[24px] accent-[#FF5E4C]"
                  />
                </div>
                <span className="text-[16px] text-black">Price: High to Low</span>
              </label>

            </div>

            {/* Apply Button */}
            <button
              onClick={() => handleSortChange(sortBy)}
              className="w-full h-[56px] bg-black text-white rounded-[14px] font-semibold flex items-center justify-center relative overflow-hidden"
            >
              <span className="text-[16px] tracking-[-0.32px]">Apply Filter</span>
              <svg className='absolute right-[10%] bottom-[-5%]' xmlns="http://www.w3.org/2000/svg" width="61" height="36" viewBox="0 0 61 36" fill="none">
                <path d="M17.8291 8.93766C17.4553 8.93766 17.0741 8.84601 16.7368 8.76537C15.8388 8.5491 13.7384 8.04692 14.2406 5.94657C14.4348 5.13283 14.8784 4.42354 15.4887 3.94885C16.1375 3.44301 16.9274 3.25424 17.6623 3.43019C19.1322 3.78208 19.99 5.48471 19.5739 7.22401C19.244 8.60224 18.5476 8.93766 17.8291 8.93766ZM17.1107 4.83226C16.7955 4.83226 16.5315 4.99538 16.3886 5.10717C16.0458 5.37476 15.7819 5.80545 15.6665 6.2893C15.5602 6.731 15.5748 6.98025 17.0777 7.33947C17.3123 7.39629 17.8053 7.51176 17.9171 7.46594C17.9171 7.46594 18.0289 7.37613 18.1462 6.88311C18.3698 5.94657 17.9923 5.0192 17.3196 4.85792C17.2482 4.84143 17.1785 4.83226 17.1107 4.83226Z" fill="#808080"/>
                <path d="M9.68653 8.10003C9.35114 8.10003 9.01389 8.05238 8.70599 7.90026C8.1305 7.61435 7.79693 7.04986 7.68879 6.17929C7.58982 5.38204 7.74927 4.59395 8.13781 3.95982C8.55385 3.28352 9.20448 2.84365 9.92659 2.75568C10.6432 2.66587 11.3855 2.93164 11.9555 3.48514C12.4888 4.00381 12.8352 4.72956 12.9342 5.52682C13.0716 6.62648 12.5694 7.21665 12.1222 7.51722C11.6182 7.85811 10.9695 7.97539 10.3995 8.04687C10.1704 8.07619 9.92846 8.10003 9.68287 8.10003H9.68653ZM10.2088 4.20173C10.1759 4.20173 10.1429 4.20357 10.108 4.20907C9.72865 4.25672 9.49224 4.55362 9.38777 4.7259C9.17517 5.07229 9.08718 5.53416 9.144 5.99602C9.18432 6.31858 9.26499 6.53851 9.35846 6.58616C9.53807 6.67597 10.0347 6.61366 10.2217 6.58983C10.5956 6.54401 11.0556 6.46888 11.303 6.30026C11.3891 6.24161 11.5358 6.14264 11.4808 5.70461C11.424 5.24275 11.2242 4.81388 10.9328 4.53164C10.8008 4.40334 10.5406 4.1999 10.2088 4.1999V4.20173Z" fill="#808080"/>
                <path d="M4.47307 12.2714C3.619 12.2714 3.12602 11.7308 2.90609 11.4045C2.08684 10.1839 2.35989 8.55825 3.51637 7.78115C4.67284 7.00589 6.28202 7.36512 7.1031 8.58574C7.52281 9.21071 7.6291 9.81002 7.422 10.369C7.21123 10.939 6.72188 11.3221 6.31867 11.5933C5.57274 12.0937 4.96425 12.2714 4.47307 12.2714ZM4.87631 8.83499C4.6857 8.83499 4.49689 8.88815 4.33377 8.99628C3.84809 9.32251 3.75281 10.0355 4.12302 10.5853C4.22199 10.7337 4.45659 11.0765 5.50127 10.3745C5.91181 10.0996 6.01994 9.93281 6.04743 9.85767C6.08958 9.74221 5.9833 9.5461 5.88617 9.40131C5.64058 9.03659 5.25203 8.83316 4.87631 8.83316V8.83499Z" fill="#808080"/>
                <path d="M22.6033 13.0027C21.9069 13.0027 21.2874 12.5867 20.8989 12.3264C20.2739 11.9049 18.3678 10.6256 19.6837 8.66823C20.7192 7.12687 22.6198 6.58254 23.9193 7.45677C25.2187 8.331 25.435 10.2939 24.3995 11.8353C23.8056 12.7205 23.1788 13.0027 22.6033 13.0027ZM22.5117 8.50146C21.9545 8.50146 21.3149 8.86618 20.9007 9.48382C20.591 9.94568 20.492 10.2847 21.7163 11.1077C22.6235 11.7161 22.7463 11.6666 23.1825 11.016C23.758 10.1601 23.7195 9.0861 23.1018 8.6719C22.9296 8.55643 22.7261 8.50146 22.5117 8.50146Z" fill="#808080"/>
                <path d="M22.7727 42.5084H4.44509C4.24348 42.5084 4.05106 42.4259 3.91177 42.2793C3.77431 42.1327 3.70098 41.9348 3.71198 41.735C3.71931 41.603 4.44325 28.4933 4.44325 22.7146C4.44325 18.8547 3.80547 16.3274 3.36744 15.0444C2.50604 14.5423 1.74727 13.822 1.16079 12.9496C-0.642656 10.2646 -0.307292 6.8703 1.90669 5.3821C2.92021 4.70214 4.20499 4.51885 5.48976 4.84325C5.75001 2.29754 7.38668 0.307158 9.58417 0.0322431C11.2776 -0.180358 12.9088 0.670053 13.9828 2.2389C15.1979 1.03661 16.7686 0.521594 18.2715 0.882649C20.0181 1.30235 21.3157 2.84371 21.6914 4.85059C22.9909 4.5042 24.2701 4.68748 25.3075 5.38393C27.5215 6.87214 27.8568 10.2664 26.0534 12.9514C25.4669 13.8256 24.7082 14.5441 23.8468 15.0463C23.4069 16.3274 22.7691 18.8529 22.7691 22.7164C22.7691 28.4933 23.493 41.6049 23.5003 41.7368C23.5113 41.9384 23.4398 42.1345 23.3006 42.2812C23.1613 42.4278 22.9688 42.5103 22.7672 42.5103L22.7727 42.5084ZM5.21851 41.0422H21.9975C21.8417 38.1244 21.3047 27.7125 21.3047 22.7146C21.3047 18.3507 22.0928 15.5594 22.562 14.2838C22.6243 14.1134 22.7489 13.9723 22.912 13.888C23.6635 13.4958 24.3288 12.8891 24.8383 12.1322C26.1909 10.1198 26.035 7.6364 24.4919 6.60089C23.6415 6.0309 22.5052 5.99973 21.3725 6.5184C21.1508 6.62104 20.8923 6.60455 20.6816 6.47625C20.4708 6.34796 20.3425 6.12253 20.3333 5.87877C20.2673 4.07166 19.3015 2.63661 17.9288 2.30855C16.7063 2.0153 15.3757 2.63845 14.4611 3.93788C14.3108 4.15048 14.0616 4.2696 13.794 4.24577C13.5319 4.22194 13.3047 4.05884 13.1947 3.82058C12.4671 2.23524 11.1237 1.32069 9.76378 1.48747C8.0648 1.70007 6.81669 3.60981 6.92482 5.83845C6.93765 6.09504 6.81302 6.3388 6.60042 6.48359C6.38599 6.62655 6.11109 6.64669 5.88016 6.53673C4.73651 5.99973 3.58737 6.02174 2.72231 6.60089C1.17912 7.63824 1.02331 10.1198 2.37589 12.1322C2.8854 12.8891 3.55069 13.4976 4.30213 13.888C4.46341 13.9723 4.58802 14.1134 4.65217 14.2857C5.12136 15.5613 5.90947 18.3544 5.90947 22.7146C5.90947 27.7125 5.37246 38.1244 5.21667 41.0422H5.21851Z" fill="#808080"/>
                <path d="M16.1762 19.7822C15.3551 19.7822 14.9995 19.5824 14.6495 19.3405C14.4314 19.1902 14.2261 19.0491 13.2438 19.0491C12.2614 19.0491 12.0561 19.1902 11.838 19.3405C11.488 19.5824 11.1324 19.7822 10.3113 19.7822C9.29598 19.7822 7.37891 18.8621 7.37891 15.3835C7.37891 12.134 10.5881 9.51868 13.2438 9.51868C15.8994 9.51868 19.1086 12.134 19.1086 15.3835C19.1086 18.8621 17.1915 19.7822 16.1762 19.7822ZM13.2438 17.5828C14.5743 17.5828 15.0454 17.8321 15.4816 18.1345C15.6612 18.2591 15.7436 18.316 16.1762 18.316C16.5849 18.316 17.6424 17.7881 17.6424 15.3835C17.6424 12.8323 15.0142 10.9849 13.2438 10.9849C11.4733 10.9849 8.84512 12.8323 8.84512 15.3835C8.84512 17.7881 9.90262 18.316 10.3113 18.316C10.742 18.316 10.8245 18.2591 11.006 18.1345C11.444 17.8339 11.9132 17.5828 13.2438 17.5828Z" fill="#808080"/>
                <path d="M42.3818 8.93766C42.7557 8.93766 43.1369 8.84601 43.4741 8.76537C44.3722 8.5491 46.4725 8.04692 45.9704 5.94657C45.7761 5.13283 45.3325 4.42354 44.7222 3.94885C44.0734 3.44301 43.2835 3.25424 42.5486 3.43019C41.0787 3.78208 40.221 5.48471 40.637 7.22401C40.9669 8.60224 41.6633 8.93766 42.3818 8.93766ZM43.1002 4.83226C43.4155 4.83226 43.6794 4.99538 43.8224 5.10717C44.1651 5.37476 44.429 5.80545 44.5445 6.2893C44.6508 6.731 44.6361 6.98025 43.1332 7.33947C42.8986 7.39629 42.4056 7.51176 42.2938 7.46594C42.2938 7.46594 42.182 7.37613 42.0647 6.88311C41.8411 5.94657 42.2187 5.0192 42.8913 4.85792C42.9628 4.84143 43.0324 4.83226 43.1002 4.83226Z" fill="#808080"/>
                <path d="M50.5283 8.10003C50.8637 8.10003 51.201 8.05238 51.5089 7.90026C52.0843 7.61435 52.4179 7.04986 52.526 6.17929C52.625 5.38204 52.4656 4.59395 52.077 3.95982C51.661 3.28352 51.0104 2.84365 50.2883 2.75568C49.5716 2.66587 48.8294 2.93164 48.2594 3.48514C47.726 4.00381 47.3796 4.72956 47.2807 5.52682C47.1432 6.62648 47.6454 7.21665 48.0926 7.51722C48.5966 7.85811 49.2454 7.97539 49.8154 8.04687C50.0445 8.07619 50.2864 8.10003 50.532 8.10003H50.5283ZM50.006 4.20173C50.039 4.20173 50.072 4.20357 50.1068 4.20907C50.4862 4.25672 50.7226 4.55362 50.8271 4.7259C51.0397 5.07229 51.1277 5.53416 51.0708 5.99602C51.0305 6.31858 50.9499 6.53851 50.8564 6.58616C50.6768 6.67597 50.1801 6.61366 49.9932 6.58983C49.6193 6.54401 49.1593 6.46888 48.9118 6.30026C48.8257 6.24161 48.6791 6.14264 48.7341 5.70461C48.7909 5.24275 48.9907 4.81388 49.2821 4.53164C49.414 4.40334 49.6743 4.1999 50.006 4.1999V4.20173Z" fill="#808080"/>
                <path d="M55.7418 12.2714C56.5958 12.2714 57.0888 11.7308 57.3088 11.4045C58.128 10.1839 57.855 8.55825 56.6985 7.78115C55.542 7.00589 53.9328 7.36512 53.1117 8.58574C52.692 9.21071 52.5857 9.81002 52.7928 10.369C53.0036 10.939 53.493 11.3221 53.8962 11.5933C54.6421 12.0937 55.2506 12.2714 55.7418 12.2714ZM55.3385 8.83499C55.5291 8.83499 55.718 8.88815 55.8811 8.99628C56.3668 9.32251 56.462 10.0355 56.0918 10.5853C55.9929 10.7337 55.7583 11.0765 54.7136 10.3745C54.303 10.0996 54.1949 9.93281 54.1674 9.85767C54.1253 9.74221 54.2315 9.5461 54.3287 9.40131C54.5743 9.03659 54.9628 8.83316 55.3385 8.83316V8.83499Z" fill="#808080"/>
                <path d="M37.6115 13.0027C38.308 13.0027 38.9274 12.5867 39.316 12.3264C39.941 11.9049 41.8471 10.6256 40.5311 8.66823C39.4956 7.12687 37.595 6.58254 36.2956 7.45677C34.9962 8.331 34.7799 10.2939 35.8154 11.8353C36.4092 12.7205 37.036 13.0027 37.6115 13.0027ZM37.7031 8.50146C38.2603 8.50146 38.8999 8.86618 39.3142 9.48382C39.6239 9.94568 39.7229 10.2847 38.4986 11.1077C37.5914 11.7161 37.4686 11.6666 37.0324 11.016C36.4569 10.1601 36.4954 9.0861 37.113 8.6719C37.2853 8.55643 37.4887 8.50146 37.7031 8.50146Z" fill="#808080"/>
                <path d="M37.4421 42.5084H55.7698C55.9714 42.5084 56.1638 42.4259 56.3031 42.2793C56.4405 42.1327 56.5139 41.9348 56.5029 41.735C56.4955 41.603 55.7716 28.4933 55.7716 22.7146C55.7716 18.8547 56.4094 16.3274 56.8474 15.0444C57.7088 14.5423 58.4676 13.822 59.0541 12.9496C60.8575 10.2646 60.5221 6.8703 58.3082 5.3821C57.2946 4.70214 56.0099 4.51885 54.7251 4.84325C54.4648 2.29754 52.8282 0.307158 50.6307 0.0322431C48.9372 -0.180358 47.306 0.670053 46.232 2.2389C45.0169 1.03661 43.4463 0.521594 41.9434 0.882649C40.1968 1.30235 38.8991 2.84371 38.5234 4.85059C37.224 4.5042 35.9447 4.68748 34.9074 5.38393C32.6934 6.87214 32.358 10.2664 34.1615 12.9514C34.748 13.8256 35.5067 14.5441 36.3681 15.0463C36.8079 16.3274 37.4458 18.8529 37.4458 22.7164C37.4458 28.4933 36.7218 41.6049 36.7145 41.7368C36.7035 41.9384 36.775 42.1345 36.9143 42.2812C37.0536 42.4278 37.246 42.5103 37.4476 42.5103L37.4421 42.5084ZM54.9963 41.0422H38.2174C38.3731 38.1244 38.9101 27.7125 38.9101 22.7146C38.9101 18.3507 38.122 15.5594 37.6529 14.2838C37.5905 14.1134 37.4659 13.9723 37.3028 13.888C36.5514 13.4958 35.8861 12.8891 35.3766 12.1322C34.024 10.1198 34.1798 7.6364 35.723 6.60089C36.5734 6.0309 37.7097 5.99973 38.8423 6.5184C39.0641 6.62104 39.3225 6.60455 39.5333 6.47625C39.744 6.34796 39.8724 6.12253 39.8815 5.87877C39.9475 4.07166 40.9133 2.63661 42.2861 2.30855C43.5085 2.0153 44.8392 2.63845 45.7537 3.93788C45.904 4.15048 46.1532 4.2696 46.4208 4.24577C46.6829 4.22194 46.9102 4.05884 47.0201 3.82058C47.7477 2.23524 49.0912 1.32069 50.4511 1.48747C52.15 1.70007 53.3982 3.60981 53.29 5.83845C53.2772 6.09504 53.4018 6.3388 53.6144 6.48359C53.8289 6.62655 54.1038 6.64669 54.3347 6.53673C55.4783 5.99973 56.6275 6.02174 57.4925 6.60089C59.0357 7.63824 59.1915 10.1198 57.839 12.1322C57.3294 12.8891 56.6642 13.4976 55.9127 13.888C55.7514 13.9723 55.6268 14.1134 55.5627 14.2857C55.0935 15.5613 54.3054 18.3544 54.3054 22.7146C54.3054 27.7125 54.8424 38.1244 54.9982 41.0422H54.9963Z" fill="#808080"/>
                <path d="M44.0387 19.7822C44.8597 19.7822 45.2153 19.5824 45.5654 19.3405C45.7835 19.1902 45.9887 19.0491 46.9711 19.0491C47.9534 19.0491 48.1587 19.1902 48.3768 19.3405C48.7269 19.5824 49.0824 19.7822 49.9035 19.7822C50.9189 19.7822 52.8359 18.8621 52.8359 15.3835C52.8359 12.134 49.6268 9.51868 46.9711 9.51868C44.3154 9.51868 41.1062 12.134 41.1062 15.3835C41.1062 18.8621 43.0233 19.7822 44.0387 19.7822ZM46.9711 17.5828C45.6405 17.5828 45.1695 17.8321 44.7333 18.1345C44.5537 18.2591 44.4712 18.316 44.0387 18.316C43.63 18.316 42.5724 17.7881 42.5724 15.3835C42.5724 12.8323 45.2006 10.9849 46.9711 10.9849C48.7415 10.9849 51.3697 12.8323 51.3697 15.3835C51.3697 17.7881 50.3122 18.316 49.9035 18.316C49.4728 18.316 49.3903 18.2591 49.2089 18.1345C48.7709 17.8339 48.3017 17.5828 46.9711 17.5828Z" fill="#808080"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      <FilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        filters={advancedFilters}
        onFiltersChange={setAdvancedFilters}
        onResetFilters={setAdvancedFilters}
        searchFilters={filters}
        cityOptions={filterCityOptions}
        hideNearMe
        resultCount={stays.length}
      />
    </div>
  );
};

export default SearchResults;
